/**
 * Staking session store for user-specific staking data
 * - Blacklist status
 * - Unstaking queue
 */

import { useSimMode } from "@shared/sim";
import { DEFAULT_SALT, TOKEN_DECIMALS } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { CacheManager } from "@solomon-labs/caching";
import type { PublicKey } from "@solomon-labs/publickey";
import { SolomonStake, UserData } from "@solomon-labs/solomon-protocol";
import { create } from "zustand";

// The on-chain UserData account pre-allocates space for 100 queue items.
// When entries are claimed and removed from the Borsh vector, residual non-zero
// bytes remain in the trailing slots. The Decoder's strict trailing-bytes check
// throws TooMuchDataDecoderError, causing fetchUserData to return null.
// Setting decodePolicy to "ignore" tells the Decoder to skip this check.
(UserData as unknown as { decodePolicy: string }).decodePolicy = "ignore";

import { fromRawAmount } from "@/services/staking/staking-calculations";
import {
    applyClaimReductionToQueue,
    buildBaseQueue,
    generateOptimisticEntryId,
    INITIAL_INTERNAL_STAKING_VAULT_STATE,
    type InternalStakingVaultState,
    reconcileVaultAfterFetch,
    RECONCILIATION_INTERVAL_MS,
    RECONCILIATION_TIMEOUT_MS,
    resetOptimisticEntryCounter,
    toPublicVaultState
} from "@/utils/stakingQueueReconciliation";

/**
 * User's unstaking queue entry
 */
export interface UserUnstakingQueueEntry {
    amount: BigDecimal;
    timestamp: number;
}

/**
 * State for a specific staking vault (keyed by salt)
 */
export interface StakingVaultState {
    isStakeBlacklistedLoaded: boolean;
    isUserUnstakingQueueLoaded: boolean;
    isStakeBlacklisted: boolean;
    userUnstakingQueue: UserUnstakingQueueEntry[] | undefined;
}

/** Result of a staking-state RPC request. */
export type StakingStateLoadResult = "loaded" | "failed" | "stale";

/**
 * Initial public state for staking vault
 */
const INITIAL_STAKING_VAULT_STATE: StakingVaultState = {
    isStakeBlacklistedLoaded: false,
    isUserUnstakingQueueLoaded: false,
    isStakeBlacklisted: false,
    userUnstakingQueue: undefined
};

/**
 * Staking session store state
 */
interface StakingSessionState {
    stakingVaults: Record<number, InternalStakingVaultState>;
    isError: boolean;
}

/**
 * Staking session store actions
 */
interface StakingSessionActions {
    getStakingState: (owner: PublicKey, salt: Uint8Array) => Promise<StakingStateLoadResult>;
    refreshStakingState: (owner: PublicKey, salt: Uint8Array) => Promise<StakingStateLoadResult>;
    applyOptimisticStartUnstake: (salt: Uint8Array, amount: BigDecimal, timestamp: number) => void;
    applyOptimisticClaim: (salt: Uint8Array, amount: BigDecimal) => void;
    stakingVault: (salt: Uint8Array) => StakingVaultState;
    reset: () => void;
}

export type StakingSessionStore = StakingSessionState & StakingSessionActions;

/**
 * Initial state
 */
const INITIAL_STATE: StakingSessionState = {
    stakingVaults: {},
    isError: false
};

let activeQueueOwner: string | null = null;
const inFlightQueueLoads = new Map<string, Promise<StakingStateLoadResult>>();
let queueLoadGeneration = 0;

export function selectStakingVaultState(
    state: Pick<StakingSessionStore, "stakingVaults">,
    salt: Uint8Array = DEFAULT_SALT
): StakingVaultState {
    const saltNum = SolomonStake.saltToNumber(salt);

    if (saltNum in state.stakingVaults) {
        return toPublicVaultState(state.stakingVaults[saltNum]);
    }

    return INITIAL_STAKING_VAULT_STATE;
}

/**
 * Staking session store for managing user-specific staking data
 */
export const useStakingSessionStore = create<StakingSessionStore>((set, get) => ({
    ...INITIAL_STATE,

    /**
     * Fetch staking state for the given owner and vault salt
     * Uses SolomonStake to fetch user-specific staking data
     */
    getStakingState: async (owner: PublicKey, salt: Uint8Array): Promise<StakingStateLoadResult> => {
        const ownerKey = owner.toString();
        let generation = queueLoadGeneration;

        if (activeQueueOwner !== null && activeQueueOwner !== ownerKey) {
            queueLoadGeneration += 1;
            generation = queueLoadGeneration;
            activeQueueOwner = ownerKey;
            set({ stakingVaults: {}, isError: false });
        } else if (activeQueueOwner === null) {
            activeQueueOwner = ownerKey;
        }
        const requestKey = `${ownerKey}:${SolomonStake.saltToNumber(salt)}:${generation}`;
        const inFlight = inFlightQueueLoads.get(requestKey);
        if (inFlight) return inFlight;
        let resolveLoad!: (result: StakingStateLoadResult) => void;
        const load = new Promise<StakingStateLoadResult>((resolve) => {
            resolveLoad = resolve;
        });
        inFlightQueueLoads.set(requestKey, load);
        const saltNum = SolomonStake.saltToNumber(salt);
        let result: StakingStateLoadResult = "stale";

        try {
            // Check if user is blacklisted
            const isBlacklisted = await SolomonStake.isUserBlacklisted(owner, salt);

            // Fetch user's unstaking queue data
            const userData = await SolomonStake.fetchUserData(owner, salt);

            // Convert queue items to our format
            let userUnstakingQueue: UserUnstakingQueueEntry[] | undefined;

            if (userData) {
                // Combine available assets and pending queue
                // Convert raw amounts (9 decimals) to human-readable BigDecimal
                const allQueueItems: UserUnstakingQueueEntry[] = userData.unstakeQueue.map((item) => ({
                    amount: fromRawAmount(item.amount, TOKEN_DECIMALS.USDv),
                    timestamp: item.endTimestamp
                }));

                // If there are available assets not in queue, they're already claimable
                if (userData.assetsAvailable > 0n) {
                    // Available assets are already past their cooldown
                    // We represent them with timestamp 0 (meaning "ready now")
                    allQueueItems.unshift({
                        amount: fromRawAmount(userData.assetsAvailable, TOKEN_DECIMALS.USDv),
                        timestamp: 0 // Already claimable
                    });
                }

                userUnstakingQueue = allQueueItems.length > 0 ? allQueueItems : undefined;
            }

            set((state) => {
                if (activeQueueOwner !== ownerKey || queueLoadGeneration !== generation) return state;
                const baseVault = state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE();
                const nextVault: InternalStakingVaultState = {
                    ...baseVault,
                    isStakeBlacklistedLoaded: true,
                    isUserUnstakingQueueLoaded: true,
                    isStakeBlacklisted: isBlacklisted,
                    fetchedUserUnstakingQueue: userUnstakingQueue
                };
                // Run reconciliation here too — not only inside the
                // refreshStakingState polling loop. Any caller that refetches
                // (e.g. WithdrawForm's mount-effect) should drop optimistic
                // entries the chain now confirms, instead of leaving them
                // visible as duplicates until the next polling tick.
                return {
                    isError: false,
                    stakingVaults: {
                        ...state.stakingVaults,
                        [saltNum]: reconcileVaultAfterFetch(nextVault)
                    }
                };
            });
            if (activeQueueOwner === ownerKey && queueLoadGeneration === generation) {
                result = "loaded";
            }
        } catch (error) {
            if (activeQueueOwner !== ownerKey || queueLoadGeneration !== generation) return result;
            console.error("Error fetching staking state:", error);
            set((state) => ({
                stakingVaults: {
                    ...state.stakingVaults,
                    [saltNum]: {
                        ...(state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE()),
                        // A failed RPC must not look like a loaded empty queue.
                        // Callers can now display recovery UI and issue a fresh,
                        // owner/generation-guarded request.
                        isStakeBlacklistedLoaded: false,
                        isUserUnstakingQueueLoaded: false
                    }
                },
                isError: true
            }));
            result = "failed";
        } finally {
            if (inFlightQueueLoads.get(requestKey) === load) inFlightQueueLoads.delete(requestKey);
            resolveLoad(result);
        }

        return result;
    },

    /**
     * Bust UserData cache entries and refetch staking state.
     * Use after successful unstake/claim transactions to ensure fresh data.
     */
    refreshStakingState: async (owner: PublicKey, salt: Uint8Array): Promise<StakingStateLoadResult> => {
        const ownerKey = owner.toString();
        if (activeQueueOwner === null) {
            activeQueueOwner = ownerKey;
        } else if (activeQueueOwner !== ownerKey) {
            return "stale";
        }
        const generation = queueLoadGeneration;
        const isCurrentSession = (): boolean => activeQueueOwner === ownerKey && queueLoadGeneration === generation;

        // A refresh is reconciliation for an already-loaded wallet. It must
        // never make an old wallet active again after a wallet switch.
        if (!isCurrentSession()) return "stale";
        const saltNum = SolomonStake.saltToNumber(salt);
        const version = (get().stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE()).mutationVersion;

        set((state) => ({
            stakingVaults: {
                ...state.stakingVaults,
                [saltNum]: {
                    ...(state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE()),
                    activeReconciliationVersion: version
                }
            }
        }));

        try {
            const [vaultStatePDA] = await SolomonStake.getVaultStatePDA(salt);
            const [userDataPDA] = await SolomonStake.getUserDataPDA(owner, vaultStatePDA);
            await CacheManager.delete(CacheManager.addressToCacheKey("UserData", userDataPDA));
            await CacheManager.delete(CacheManager.addressToCacheKey("account", userDataPDA));
        } catch {
            // Cache bust failure is non-fatal
        }

        if (!isCurrentSession()) return "stale";

        const deadline = Date.now() + RECONCILIATION_TIMEOUT_MS;

        while (Date.now() <= deadline) {
            if (!isCurrentSession()) return "stale";
            const currentVault = get().stakingVaults[saltNum];
            if (currentVault && currentVault.mutationVersion !== version) {
                return "stale";
            }

            const loadResult = await get().getStakingState(owner, salt);

            if (!isCurrentSession()) return "stale";
            if (loadResult !== "loaded") return loadResult;

            const reconciledVault = get().stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE();
            if (
                reconciledVault.mutationVersion !== version ||
                reconciledVault.activeReconciliationVersion !== version
            ) {
                return "loaded";
            }

            if (
                reconciledVault.optimisticQueueEntries.length === 0 &&
                !reconciledVault.optimisticClaimReduction.gt(0)
            ) {
                set((state) => {
                    if (!isCurrentSession()) return state;
                    const latestVault = state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE();
                    if (latestVault.activeReconciliationVersion !== version) {
                        return state;
                    }

                    return {
                        stakingVaults: {
                            ...state.stakingVaults,
                            [saltNum]: {
                                ...latestVault,
                                activeReconciliationVersion: null
                            }
                        }
                    };
                });
                return "loaded";
            }

            if (Date.now() + RECONCILIATION_INTERVAL_MS > deadline) {
                break;
            }

            await new Promise((resolve) => setTimeout(resolve, RECONCILIATION_INTERVAL_MS));
        }

        set((state) => {
            if (!isCurrentSession()) return state;
            const currentVault = state.stakingVaults[saltNum];
            if (currentVault?.activeReconciliationVersion !== version) {
                return state;
            }

            return {
                stakingVaults: {
                    ...state.stakingVaults,
                    [saltNum]: {
                        ...currentVault,
                        activeReconciliationVersion: null
                    }
                }
            };
        });
        return isCurrentSession() ? "loaded" : "stale";
    },

    applyOptimisticStartUnstake: (salt: Uint8Array, amount: BigDecimal, timestamp: number): void => {
        const saltNum = SolomonStake.saltToNumber(salt);

        set((state) => {
            const currentVault = state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE();

            return {
                stakingVaults: {
                    ...state.stakingVaults,
                    [saltNum]: {
                        ...currentVault,
                        isUserUnstakingQueueLoaded: true,
                        optimisticQueueEntries: [
                            ...currentVault.optimisticQueueEntries,
                            {
                                id: generateOptimisticEntryId(),
                                amount,
                                timestamp,
                                createdAt: Date.now()
                            }
                        ],
                        mutationVersion: currentVault.mutationVersion + 1
                    }
                }
            };
        });
    },

    applyOptimisticClaim: (salt: Uint8Array, amount: BigDecimal): void => {
        const saltNum = SolomonStake.saltToNumber(salt);

        set((state) => {
            const currentVault = state.stakingVaults[saltNum] ?? INITIAL_INTERNAL_STAKING_VAULT_STATE();

            return {
                stakingVaults: {
                    ...state.stakingVaults,
                    [saltNum]: {
                        ...currentVault,
                        optimisticClaimReduction: currentVault.optimisticClaimReduction.add(amount),
                        optimisticClaimTargetQueue: applyClaimReductionToQueue(
                            buildBaseQueue(currentVault.fetchedUserUnstakingQueue, currentVault.optimisticQueueEntries),
                            currentVault.optimisticClaimReduction.add(amount)
                        ),
                        isUserUnstakingQueueLoaded: true,
                        mutationVersion: currentVault.mutationVersion + 1
                    }
                }
            };
        });
    },

    /**
     * Get staking vault state by salt
     */
    stakingVault: (salt: Uint8Array = DEFAULT_SALT): StakingVaultState => {
        return selectStakingVaultState(get(), salt);
    },

    /**
     * Reset store to initial state
     */
    reset: (): void => {
        queueLoadGeneration += 1;
        activeQueueOwner = null;
        inFlightQueueLoads.clear();
        resetOptimisticEntryCounter();
        set(INITIAL_STATE);
    }
}));

// Sim-mode mock: a single matured queue entry so WithdrawForm has
// something to claim. `timestamp: 0` makes the cooldown predicate
// always treat it as available.
const SIM_VAULT_STATE: StakingVaultState = {
    isStakeBlacklistedLoaded: true,
    isUserUnstakingQueueLoaded: true,
    isStakeBlacklisted: false,
    userUnstakingQueue: [{ amount: new BigDecimal("500.00"), timestamp: 0 }]
};

export function useStakingVaultState(salt: Uint8Array = DEFAULT_SALT): StakingVaultState {
    const sim = useSimMode();
    const saltNum = SolomonStake.saltToNumber(salt);
    const vaultState = useStakingSessionStore((state) => {
        if (saltNum in state.stakingVaults) {
            return toPublicVaultState(state.stakingVaults[saltNum]);
        }

        return INITIAL_STAKING_VAULT_STATE;
    });

    return sim.enabled ? SIM_VAULT_STATE : vaultState;
}
